import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { NetworkIcon } from '@/components/NetworkIcons';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';

export interface ExternalWallet {
  id: string;
  address: string;
  address_preview: string; // e.g., "0x1234...5678"
  chain: string;
  currency: string;
  label?: string;
  status: 'active' | 'pending' | 'disabled';
  is_verified: boolean;
}

interface ExternalWalletCardProps {
  wallet: ExternalWallet;
  isSelected: boolean;
  onClick: () => void;
}

// Chain display names
const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  polygon: 'Polygon',
  solana: 'Solana',
  base: 'Base',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  avalanche: 'Avalanche',
  tron: 'Tron',
  stellar: 'Stellar',
  bitcoin: 'Bitcoin',
};

export function ExternalWalletCard({
  wallet,
  isSelected,
  onClick,
}: ExternalWalletCardProps) {
  const isActive = wallet.status === 'active';
  const chainLabel = CHAIN_LABELS[wallet.chain.toLowerCase()] || wallet.chain;

  return (
    <Card
      className={cn(
        "cursor-pointer min-h-[72px]",
        semanticTokens.motion.standard,
        semanticTokens.focus.ring,
        "hover:shadow-md",
        isSelected 
          ? 'ring-2 ring-primary ring-offset-2 border-primary' 
          : semanticTokens.border.default,
        !isActive && 'opacity-60 cursor-not-allowed',
        semanticTokens.bg.surface,
        semanticTokens.shadow.sm,
        semanticTokens.radius.lg,
        "focus-visible:outline-none"
      )}
      onClick={() => isActive && onClick()}
      role="button"
      tabIndex={0}
      aria-label={`Select ${wallet.label || 'external wallet'} on ${chainLabel}`}
      aria-pressed={isSelected}
      aria-disabled={!isActive}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && isActive) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardContent className={cn(
        "p-4 flex justify-between items-center",
        semanticTokens.spacing.md
      )}>
        <div className={cn("flex items-center gap-3", semanticTokens.gap.sm)}>
          {/* Chain Icon with Currency Badge */}
          <div className="relative" aria-hidden="true">
            <div className={cn(
              "w-12 h-12",
              semanticTokens.bg.muted,
              semanticTokens.radius.full,
              "flex items-center justify-center"
            )}>
              <NetworkIcon network={wallet.chain} className="w-6 h-6" />
            </div>
            {/* Currency badge overlay */}
            <div className={cn(
              "absolute -bottom-1 -right-1",
              "w-5 h-5",
              semanticTokens.bg.surface,
              semanticTokens.radius.full,
              "flex items-center justify-center",
              "ring-2 ring-white dark:ring-slate-900"
            )}>
              <CurrencyIcon symbol={wallet.currency} size="xs" />
            </div>
          </div>

          {/* Wallet Info */}
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(
                semanticTokens.typography.bodyLarge,
                semanticTokens.fg.default,
                "font-medium"
              )}>
                {wallet.label || 'External Wallet'}
              </span>
              {!wallet.is_verified && (
                <AlertTriangle 
                  className={cn("w-4 h-4 shrink-0", semanticTokens.warning.accent)} 
                  aria-label="Unverified wallet"
                />
              )}
            </div>
            
            <div className={cn(
              "flex items-center gap-2",
              semanticTokens.typography.caption,
              semanticTokens.fg.muted
            )}>
              <span className="font-mono truncate max-w-[140px] sm:max-w-[180px]">
                {wallet.address_preview}
              </span>
              <span className="text-border">•</span>
              <span className="flex items-center gap-1">
                <NetworkIcon network={wallet.chain} className="w-3 h-3" />
                {chainLabel}
              </span>
            </div>
            
            {/* Asset */}
            <div className={cn(
              "flex items-center gap-1 mt-0.5",
              semanticTokens.typography.caption,
              semanticTokens.fg.subtle
            )}>
              <CurrencyIcon symbol={wallet.currency} size="xs" />
              <span>{wallet.currency}</span>
            </div>
          </div>
        </div>

        {/* Status & Selection Indicator */}
        <div className="flex items-center gap-2 shrink-0">
          {wallet.is_verified ? (
            <Badge 
              variant="secondary" 
              className={cn(
                semanticTokens.positive.bg,
                semanticTokens.positive.fg,
                semanticTokens.typography.caption,
                "gap-1"
              )}
            >
              <CheckCircle className="w-3 h-3" />
              Verified
            </Badge>
          ) : (
            <Badge 
              variant="secondary" 
              className={cn(
                semanticTokens.warning.bg,
                semanticTokens.warning.fg,
                semanticTokens.typography.caption
              )}
            >
              Unverified
            </Badge>
          )}
          {isSelected && (
            <div className={cn(
              "w-6 h-6",
              semanticTokens.positive.bg,
              semanticTokens.radius.full,
              "flex items-center justify-center"
            )}>
              <CheckCircle 
                className={cn("w-4 h-4", semanticTokens.positive.accent)} 
                aria-hidden="true" 
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// New Wallet Entry Card
interface NewWalletEntryProps {
  chain: string;
  currency: string;
  address: string;
  onAddressChange: (address: string) => void;
  isValid: boolean;
  validationMessage?: string;
}

export function NewWalletEntry({
  chain,
  currency,
  address,
  onAddressChange,
  isValid,
  validationMessage,
}: NewWalletEntryProps) {
  const chainLabel = CHAIN_LABELS[chain.toLowerCase()] || chain;

  return (
    <div className={cn(
      "space-y-3",
      semanticTokens.bg.muted,
      semanticTokens.spacing.md,
      semanticTokens.radius.lg,
      "border",
      semanticTokens.border.default
    )}>
      <div className="flex items-center gap-2">
        <NetworkIcon network={chain} className="w-5 h-5" />
        <span className={cn(semanticTokens.typography.label, semanticTokens.fg.default)}>
          {chainLabel} Address
        </span>
        <Badge variant="secondary" className={cn(semanticTokens.typography.caption)}>
          <CurrencyIcon symbol={currency} size="xs" className="mr-1" />
          {currency}
        </Badge>
      </div>
      
      <div className="relative">
        <input
          type="text"
          value={address}
          onChange={(e) => onAddressChange(e.target.value)}
          placeholder={`Enter your ${chainLabel} wallet address`}
          className={cn(
            "w-full",
            semanticTokens.typography.code,
            "px-3 py-2.5 min-h-[44px]",
            semanticTokens.bg.surface,
            semanticTokens.radius.md,
            semanticTokens.motion.standard,
            "border",
            !address ? semanticTokens.border.default :
              isValid ? semanticTokens.positive.border : semanticTokens.danger.border,
            semanticTokens.focus.ring,
            semanticTokens.fg.default,
            "placeholder:text-muted-foreground/60"
          )}
          aria-label={`${chainLabel} wallet address`}
          aria-invalid={address && !isValid}
          aria-describedby={validationMessage ? "address-validation" : undefined}
        />
        {address && (
          <div className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2"
          )}>
            {isValid ? (
              <CheckCircle className={cn("w-4 h-4", semanticTokens.positive.accent)} aria-hidden="true" />
            ) : (
              <AlertTriangle className={cn("w-4 h-4", semanticTokens.danger.accent)} aria-hidden="true" />
            )}
          </div>
        )}
      </div>
      
      {validationMessage && (
        <p 
          id="address-validation"
          className={cn(
            semanticTokens.typography.caption,
            isValid ? semanticTokens.positive.fg : semanticTokens.danger.fg
          )}
        >
          {validationMessage}
        </p>
      )}
      
      <div className={cn(
        "flex items-start gap-2 p-2",
        semanticTokens.warning.bg,
        semanticTokens.radius.md,
        semanticTokens.warning.border,
        "border"
      )}>
        <AlertTriangle className={cn("w-4 h-4 shrink-0 mt-0.5", semanticTokens.warning.accent)} />
        <p className={cn(semanticTokens.typography.caption, semanticTokens.warning.fg)}>
          Double-check the address. Crypto transactions are irreversible. Sending to an incorrect 
          address will result in permanent loss of funds.
        </p>
      </div>
    </div>
  );
}

